import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import HomeBtn from "./Instruments/HomeBtn";
import { getDecoderOwner, getUser, addDecoder } from "../../../shared/service";

/**
 * Entry point for the decoder QR code. The QR encodes a plain deep link
 *   https://<host>/add-dcr?dcr_id=37-511893
 * so any phone camera opens it (no in-app scanner) and this page decides where
 * to send the client, per the decoder lookup contract:
 *
 *   GET /api/user/decoder/{id} -> { result, user_id, phone }
 *     - not found (result != OK)        -> error screen
 *     - registered (user_id != null)    -> /pay  (payment provider selection)
 *     - in DB, no owner (user_id null)  -> attach the decoder to an account:
 *         * logged in     -> PUT decoder, go to balance
 *         * not logged in -> stash the id and go to login (attached after sign-in)
 */
const AddDecoder = ({ onPendingDecoder }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  // Guard against a double run (e.g. StrictMode) so we never PUT the decoder twice.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const resolve = async () => {
      const id = (searchParams.get("dcr_id") || "").trim();
      if (!id) {
        setError("No decoder id provided");
        return;
      }

      let owner;
      try {
        owner = await getDecoderOwner(id);
      } catch (e) {
        console.error("Decoder lookup failed:", e);
        setError("Decoder not found or service unavailable");
        return;
      }

      if (owner?.result !== "OK") {
        // Decoder is not in the database -> error.
        setError(owner?.result || "Decoder not found");
        return;
      }

      if (owner.user_id != null) {
        // Already registered to a subscriber -> straight to the payment page.
        navigate(`/pay?dcr_id=${encodeURIComponent(id)}`, { replace: true });
        return;
      }

      // In the database but not attached to anyone -> add it to an account.
      let loggedIn = false;
      try {
        await getUser();
        loggedIn = true;
      } catch {
        loggedIn = false;
      }

      if (!loggedIn) {
        // Hand the id to the router so Login attaches it right after sign-in.
        onPendingDecoder(id);
        navigate("/login", { replace: true });
        return;
      }

      try {
        await addDecoder(id);
        navigate("/balance", { replace: true });
      } catch (e) {
        console.error("Attaching decoder failed:", e);
        setError("Could not add this decoder, please try again");
      }
    };

    resolve();
  }, [searchParams, navigate, onPendingDecoder]);

  return (
    <div>
      <div className="main for-log">
        <HomeBtn />
        <div className="log">
          <div className="logs">
            {error ? (
              <>
                <h2 className="main-pages-titles">Decoder error</h2>
                <p className="error-message">{error}</p>
                <div className="log-container">
                  <button
                    className="grid-log login-accept-btn"
                    onClick={() => navigate("/")}
                  >
                    Home
                  </button>
                </div>
              </>
            ) : (
              <h2 className="main-pages-titles">Checking decoder…</h2>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDecoder;
